import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

/** Admin CRUD for the full album catalog: 8 reusable attribute lists (type/size/pages/
 * cover/color/paper/laminate/personalization) + the sellable "líneas de álbum" (SKUs)
 * that combine them. Mirrors the same load/submit/reorder pattern as usePackageManager. */
export const useAlbumAdmin = () => {
  const [attributes, setAttributes] = useState({});
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/album-catalog");
    setAttributes(data.attributes);
    setLines(data.lines);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createAttribute = async (kind, label, subtitle = "") => {
    try {
      await api.post("/admin/album-attributes", { kind, label, subtitle });
      toast.success("Opción agregada");
      await load();
    } catch {
      toast.error("Error al agregar la opción");
    }
  };

  const updateAttribute = async (id, fields) => {
    try {
      await api.put(`/admin/album-attributes/${id}`, fields);
      await load();
    } catch {
      toast.error("Error al actualizar la opción");
    }
  };

  const reorderAttributes = async (kind, orderedIds) => {
    await api.put("/admin/album-attributes/reorder", { ordered_ids: orderedIds });
    await load();
  };

  const moveAttribute = async (kind, id, direction) => {
    const list = attributes[kind] || [];
    const idx = list.findIndex((a) => a.id === id);
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= list.length) return;
    const ids = list.map((a) => a.id);
    [ids[idx], ids[targetIdx]] = [ids[targetIdx], ids[idx]];
    await reorderAttributes(kind, ids);
  };

  const createLine = async (payload) => {
    try {
      await api.post("/admin/album-lines", payload);
      toast.success("Línea de álbum creada");
      await load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear la línea");
      return false;
    }
  };

  const updateLine = async (id, payload) => {
    try {
      await api.put(`/admin/album-lines/${id}`, payload);
      toast.success("Línea actualizada");
      await load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar la línea");
      return false;
    }
  };

  const toggleLineField = async (id, field, value) => {
    await api.put(`/admin/album-lines/${id}`, { [field]: value });
    await load();
  };

  const bulkUpdatePrices = async (updates) => {
    try {
      await api.put("/admin/album-lines/bulk-price", { updates });
      toast.success("Precios actualizados");
      await load();
    } catch {
      toast.error("Error al actualizar precios en lote");
    }
  };

  const moveLine = async (id, direction) => {
    const idx = lines.findIndex((l) => l.id === id);
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= lines.length) return;
    const ids = lines.map((l) => l.id);
    [ids[idx], ids[targetIdx]] = [ids[targetIdx], ids[idx]];
    await api.put("/admin/album-lines/reorder", { ordered_ids: ids });
    await load();
  };

  return {
    attributes, lines, loading, load,
    createAttribute, updateAttribute, moveAttribute,
    createLine, updateLine, toggleLineField, bulkUpdatePrices, moveLine,
  };
};
